import type { Entry } from '../../../features/admin/modules/modules.model';
import type { CreateRunnerFieldRules } from '../create-form-modal.types';
import type { DocumentTemplateMetadataRequirement } from './document-template-field-rules.types';

/**
 * The metadata a document template can demand per document: the `vereis…` flag on the template,
 * the key the document record stores that value under, the word the user is reminded with, and
 * when the value counts as filled in.
 *
 * One table for every consumer that must not drift apart: the checklist's own reminder afterwards
 * (`#missingMetadataFields`), the rule imposed beforehand on the create form
 * ({@link fieldRulesForDocumentTemplate}), and the Beheer → Documenten card surface.
 */
export const TEMPLATE_METADATA_REQUIREMENTS = [
  { flag: 'vereisEinddatum', key: 'end-date', label: 'einddatum', isFilled: (value: unknown) => !!value },
  {
    flag: 'vereisDocumentnummer',
    key: 'document-number',
    label: 'documentnummer',
    isFilled: (value: unknown) => !!(value ?? '').toString().trim(),
  },
  {
    flag: 'vereisAantalVoertuigen',
    key: 'aantal',
    label: 'aantal voertuigen',
    isFilled: (value: unknown) => value != null && value !== '',
  },
] as const satisfies readonly DocumentTemplateMetadataRequirement[];

/**
 * Whether a document template demands the metadata behind `flag`. Both shapes count: the admin UI
 * writes the string toggle `'true'`, while seeded and MCP-written templates carry a real boolean.
 * A strict `=== 'true'` silently let every boolean template demand nothing — no asterisk, and the
 * question hidden — which is the opposite of what the template says.
 */
export function templateDemands(template: Record<string, unknown> | null | undefined, flag: string): boolean {
  const raw = template?.[flag];
  return raw === true || raw === 'true';
}

/**
 * Translates one document template's demands into field rules for the records module's create
 * form: a demanded metadata question becomes required (asterisk, blocks saving), a question the
 * template does not demand is hidden so the user need not guess at it. The generated form is
 * module-wide and cannot know this distinction — only the chosen template does (#21299).
 *
 * Shared by the required-documents checklist and the Beheer → Documenten cards, which open the
 * very same create form: a rule that applied on one surface only would let the other write a
 * record the first surface then refuses.
 */
export function fieldRulesForDocumentTemplate(template: Entry | null | undefined): CreateRunnerFieldRules {
  const bag = template?.entry ?? null;
  const required: string[] = [];
  const hidden: string[] = [];
  for (const requirement of TEMPLATE_METADATA_REQUIREMENTS) {
    (templateDemands(bag, requirement.flag) ? required : hidden).push(requirement.key);
  }
  return { required, hidden };
}
